import {
    AppNotificationType,
    // AppStoreType,
    Automation,
    AutomationType,
    BetaTestingStyle,
    IntegrationCapability,
    ReleaseStepType,
    AppStoreDefaultSettings,
} from '@/types';
import { StepDependencies } from '@/types/workflow';
import { Nullable } from '@/typescriptHelpers';
import { Destination } from './destinations';

type AutomationSettingsByType = Record<AutomationType, Automation[]>;
type NotificationSettingsByType = Record<AppNotificationType, Notification[]>;

export type StepConfig =
    | BetaTestingStepConfig
    | MetadataStepConfig
    | StoreReviewStepConfig
    | ReleaseStepConfig
    | SubmissionStepConfig
    | DefaultStepConfig; // catch all for all stepConfig types with no settings data

export interface StepConfigSummary {
    id: string;
    name: string;
    description: Nullable<string>;
    archetype: ReleaseStepType;
    destinations: Destination[];
}

export interface StepConfigBase extends StepConfigSummary {
    createdAt: string;
    updatedAt: string;
    deletedAt?: string;
    dependencies: StepDependencies;

    requiredCapabilities: IntegrationCapability[];
    optionalCapabilities: IntegrationCapability[];

    integrationInstallationIds: string[];

    automations: AutomationSettingsByType;
    notifications: NotificationSettingsByType;

    tagConfigId: Nullable<string>;
    branchConfigId: Nullable<string>;
    featureAffiliationConfigId: Nullable<string>;
}

export interface DefaultStepConfig extends StepConfigBase {
    // any other step type except the ones below
    archetype: ReleaseStepType;
    settings: null;
}

export interface BetaTestingStepConfig extends StepConfigBase {
    archetype: ReleaseStepType.BetaTesting;
    settings: BetaTestingStepSettings;
}

export interface BetaTestingStepSettings {
    betaTestingStyle: BetaTestingStyle;
    prescribedBetaSoakDurationSeconds: number;
    googleTestingTrackName: Nullable<string>;
    upstreamCIStepConfigId: Nullable<string>;
    upstreamCIStepConfigOptions: Record<string, StepConfigSummary>;
}

export interface StepConfigSharedSettings {
    branchConfigId: Nullable<string>;
    tagConfigId: Nullable<string>;
    featureAffiliationConfigId: Nullable<string>;
}

export interface BetaTestingStepSettingsRequestValues extends StepConfigSharedSettings {
    betaTestingStyle: BetaTestingStyle;
    betaSoakDurationSeconds: number;
    testingTrack: Nullable<string>;
    upstreamCIStepConfigId: Nullable<string>;
}

export interface MetadataStepConfig extends StepConfigBase {
    archetype: ReleaseStepType.Metadata;
    settings: MetadataStepSettings;
}

export interface DefaultMetadataLocale {
    id: string;
    locale: string;
    displayLocale: string;
    whatsNew: Nullable<string>;
}

export interface MetadataStepSettings {
    upstreamBetaStepConfigId: string;
    upstreamBetaStepConfigOptions: Record<string, StepConfigSummary>;
    defaultMetadata: DefaultMetadataLocale[];
}

export interface StepMetadataLocaleData {
    whatsNew: Nullable<string>;
}

export interface MetadataStepSettingsRequestValues extends StepConfigSharedSettings {
    upstreamBetaStepConfigId: string;
    defaultMetadataByLocale: Record<string, StepMetadataLocaleData>;
}

export interface SubmissionStepConfig extends StepConfigBase {
    archetype: ReleaseStepType.Submission;
    settings: SubmissionStepSettings;
}

export interface SubmissionStepSettings extends ReleaseStepSettings {}

export interface SubmissionStepSettingsRequestValues extends ReleaseStepSettingsRequestValues {}

export interface StoreReviewStepConfig extends StepConfigBase {
    archetype: ReleaseStepType.StoreReview;
    settings: StoreReviewStepSettings;
}

export interface StoreReviewStepSettingsRequestValues extends ReleaseStepSettingsRequestValues {}

export interface StoreReviewStepSettings extends ReleaseStepSettings {}

export interface ReleaseStepConfig extends StepConfigBase {
    archetype: ReleaseStepType.Takeoff;
    settings: ReleaseStepSettings;
}

export interface ReleaseStepSettingsRequestValues extends StepConfigSharedSettings {
    appStoreSettings: AppStoreDefaultSettings;
    encryptionSelection: Nullable<InitialEncryptionOptions>;
    isAppAvailableInFrance: Nullable<boolean>;
    upstreamCIStepConfigId: Nullable<string>;
}

export enum InitialEncryptionOptions {
    ProprietaryAlgorithms = 'ProprietaryAlgorithms',
    StandardAlgorithms = 'StandardAlgorithms',
    BothAlgorithms = 'BothAlgorithms',
    NoAlgorithms = 'NoAlgorithms',
}

export interface ReleaseStepSettings {
    appStoreSettings: AppStoreDefaultSettings;
    encryptionSelection: Nullable<InitialEncryptionOptions>;
    isAppAvailableInFrance: Nullable<boolean>;
    upstreamCIStepConfigId: Nullable<string>;
    upstreamCIStepConfigOptions: Record<string, StepConfigSummary>;
}
